// DEV-ONLY fixture for the public NHSO dashboard.
//
// The real data source is GET /nhsoPublicDashboard. This module exists purely so
// the page can be built and visually verified before the backend endpoint is
// deployed. It is reached only via an explicit `?mock=1` flag or as a
// non-production fallback when the live request fails (see stores/nhsoDashboard).
// It must never be part of the production render path.

const ZONE_NAMES = {
  1: "เขต 1 เชียงใหม่", 2: "เขต 2 พิษณุโลก", 3: "เขต 3 นครสวรรค์", 4: "เขต 4 สระบุรี",
  5: "เขต 5 ราชบุรี", 6: "เขต 6 ระยอง", 7: "เขต 7 ขอนแก่น", 8: "เขต 8 อุดรธานี",
  9: "เขต 9 นครราชสีมา", 10: "เขต 10 อุบลราชธานี", 11: "เขต 11 สุราษฎร์ธานี",
  12: "เขต 12 สงขลา", 13: "เขต 13 กรุงเทพมหานคร",
};

const ZONE_PROVINCES = {
  1: ["เชียงใหม่", "เชียงราย", "ลำปาง", "ลำพูน", "แพร่", "น่าน", "พะเยา", "แม่ฮ่องสอน"],
  2: ["พิษณุโลก", "ตาก", "เพชรบูรณ์", "สุโขทัย", "อุตรดิตถ์"],
  3: ["นครสวรรค์", "กำแพงเพชร", "พิจิตร", "อุทัยธานี", "ชัยนาท"],
  4: ["สระบุรี", "นนทบุรี", "ปทุมธานี", "พระนครศรีอยุธยา", "อ่างทอง", "ลพบุรี", "สิงห์บุรี", "นครนายก"],
  5: ["ราชบุรี", "กาญจนบุรี", "นครปฐม", "สุพรรณบุรี", "ประจวบคีรีขันธ์", "เพชรบุรี", "สมุทรสงคราม", "สมุทรสาคร"],
  6: ["ชลบุรี", "ระยอง", "จันทบุรี", "ตราด", "สมุทรปราการ", "ฉะเชิงเทรา", "ปราจีนบุรี", "สระแก้ว"],
  7: ["ขอนแก่น", "กาฬสินธุ์", "มหาสารคาม", "ร้อยเอ็ด"],
  8: ["อุดรธานี", "เลย", "หนองคาย", "หนองบัวลำภู", "บึงกาฬ", "นครพนม", "สกลนคร"],
  9: ["นครราชสีมา", "ชัยภูมิ", "บุรีรัมย์", "สุรินทร์"],
  10: ["อุบลราชธานี", "ศรีสะเกษ", "ยโสธร", "อำนาจเจริญ", "มุกดาหาร"],
  11: ["สุราษฎร์ธานี", "ชุมพร", "นครศรีธรรมราช", "พังงา", "ภูเก็ต", "ระนอง", "กระบี่"],
  12: ["สงขลา", "สตูล", "ตรัง", "พัทลุง", "ปัตตานี", "ยะลา", "นราธิวาส"],
  13: ["กรุงเทพมหานคร"],
};

const REGION_PROVINCES = {
  "เหนือ": ["เชียงราย", "เชียงใหม่", "น่าน", "พะเยา", "แพร่", "แม่ฮ่องสอน", "ลำปาง", "ลำพูน", "อุตรดิตถ์"],
  "กลาง": ["กรุงเทพมหานคร", "กำแพงเพชร", "ชัยนาท", "นครนายก", "นครปฐม", "นครสวรรค์", "นนทบุรี", "ปทุมธานี", "พระนครศรีอยุธยา", "พิจิตร", "พิษณุโลก", "เพชรบูรณ์", "ลพบุรี", "สมุทรปราการ", "สมุทรสงคราม", "สมุทรสาคร", "สระบุรี", "สิงห์บุรี", "สุโขทัย", "สุพรรณบุรี", "อ่างทอง", "อุทัยธานี"],
  "ตะวันออกเฉียงเหนือ": ["กาฬสินธุ์", "ขอนแก่น", "ชัยภูมิ", "นครพนม", "นครราชสีมา", "บึงกาฬ", "บุรีรัมย์", "มหาสารคาม", "มุกดาหาร", "ยโสธร", "ร้อยเอ็ด", "เลย", "ศรีสะเกษ", "สกลนคร", "สุรินทร์", "หนองคาย", "หนองบัวลำภู", "อำนาจเจริญ", "อุดรธานี", "อุบลราชธานี"],
  "ตะวันออก": ["จันทบุรี", "ฉะเชิงเทรา", "ชลบุรี", "ตราด", "ปราจีนบุรี", "ระยอง", "สระแก้ว"],
  "ตะวันตก": ["กาญจนบุรี", "ตาก", "ประจวบคีรีขันธ์", "เพชรบุรี", "ราชบุรี"],
  "ใต้": ["กระบี่", "ชุมพร", "ตรัง", "นครศรีธรรมราช", "นราธิวาส", "ปัตตานี", "พังงา", "พัทลุง", "ภูเก็ต", "ยะลา", "ระนอง", "สงขลา", "สตูล", "สุราษฎร์ธานี"],
};

const TREATMENTS = [
  "ขูดหินปูนทั้งปาก", "อุดฟันวัสดุสีเหมือนฟัน 1 ด้าน", "ตรวจสุขภาพช่องปากโดยละเอียด",
  "ถอนฟันแท้", "อุดฟันวัสดุสีเหมือนฟัน 2 ด้านขึ้นไป", "เคลือบฟลูออไรด์ชนิดเจล/โฟม",
  "ทาฟลูออไรด์วานิช", "อุดฟันอมัลกัม 1 ด้าน", "เคลือบหลุมร่องฟัน", "ถอนฟันน้ำนม",
  "เอกซเรย์ฟิล์มเดี่ยวในช่องปาก", "อุดฟันอมัลกัม 2 ด้านขึ้นไป", "ขูดหินปูนบางส่วน",
  "ผ่า/ถอนฟันคุด", "รักษาแผลถอนฟัน/เย็บแผล", "กรอแต่งฟัน", "รักษาโพรงประสาทฟันน้ำนม",
  "เอกซเรย์พาโนรามิก", "จ่ายยา/ให้คำแนะนำ", "อื่น ๆ",
];
const TREAT_WEIGHTS = [
  92750, 33400, 19600, 17900, 15200, 14300, 11200, 6900, 6400, 4300,
  3200, 2300, 1450, 1100, 640, 420, 330, 210, 160, 120,
];

const DIAGS = [
  "K02.1", "K05.1", "K03.0", "K04.7", "K08.1", "K01.1", "Z01.2", "K00.6",
  "K12.0", "K06.1", "K07.3", "K13.7", "S02.5", "K09.0", "K10.1", "K11.7",
  "K14.0", "B00.2", "B37.0", "K05.6",
];
const DIAG_WEIGHTS = [
  38400, 32150, 14020, 11300, 7410, 5900, 4600, 3200, 2900, 2100,
  1400, 1150, 980, 800, 640, 510, 350, 290, 240, 180,
];

const AGE_BANDS = ["0-14", "15-24", "25-44", "45-59", "60+", "ไม่ระบุ"];
const AGE_WEIGHTS = { "0-14": 18, "15-24": 21, "25-44": 35, "45-59": 17, "60+": 8, "ไม่ระบุ": 1 };

const AREA_SCALE = { all: 1, zone: 0.14, region: 0.26, province: 0.055 };
const SUBITEM_SCALE = { ALL: 1, D10: 0.82, D11: 0.11, D20: 0.07 };

function periodScale(period) {
  if (period === "MTD") return 0.09;
  if (period === "YTD") return 0.55;
  if (period === "ALL") return 2.4;
  if (/^CY/.test(period)) return 0.9;
  if (/^FY/.test(period)) return 1.0;
  if (/^\d{4}-\d{2}$/.test(period)) return 0.08;
  return 1; // L12M
}

function trendMonths() {
  const out = [];
  const now = new Date();
  for (let i = 11; i >= 0; i -= 1) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    out.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`);
  }
  return out;
}

// Deterministic wobble so different filters produce visibly different values.
function seedFrom(str) {
  let h = 2166136261;
  for (let i = 0; i < str.length; i += 1) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return (h >>> 0) / 4294967295;
}

// Suppress the smallest cells to "<5" to prove the suppression path renders.
const supp = n => (n > 0 && n < 5 ? "<5" : n);

function buildAreaRanking(areaType, area, scale) {
  let rows;
  if (areaType === "zone") {
    const list = ZONE_PROVINCES[Number(area)] || ZONE_PROVINCES[1];
    rows = list.map((name, i) => ({ name, base: 9000 / (i + 1.2) }));
  } else if (areaType === "region") {
    const list = REGION_PROVINCES[area] || REGION_PROVINCES["กลาง"];
    rows = list.map((name, i) => ({ name, base: 14000 / (i + 1.3) }));
  } else if (areaType === "province") {
    // No sub-level below a province — compare all provinces nationally.
    rows = Object.values(ZONE_PROVINCES).flat().map((name, i) => ({ name, base: 20000 / (i + 1.1) }));
  } else {
    rows = Object.keys(ZONE_NAMES).map(z => ({ name: ZONE_NAMES[z], base: 6000 + Number(z) * 400 }));
  }
  return rows
    .map(r => {
      const jitter = 0.7 + seedFrom(r.name + area) * 0.6;
      return { name: r.name, visits: supp(Math.round(r.base * jitter * scale)) };
    })
    .sort((a, b) => (typeof b.visits === "number" ? b.visits : 0) - (typeof a.visits === "number" ? a.visits : 0));
}

function provincesCount(areaType, area) {
  if (areaType === "zone") return (ZONE_PROVINCES[Number(area)] || []).length;
  if (areaType === "region") return (REGION_PROVINCES[area] || []).length;
  if (areaType === "province") return 1;
  return 45;
}

/**
 * Build a payload that mirrors the frozen GET /nhsoPublicDashboard contract.
 * @param {{period:string, areaType:string, area:string, subitem:string}} filters
 */
export function buildFixture(filters = {}) {
  const { period = "L12M", areaType = "all", area = "ALL", subitem = "ALL" } = filters;
  // dev fixture handles a single area — use the first of a multi-select list
  const firstArea = String(area).split(",").filter(Boolean)[0] || "ALL";
  const scale = (AREA_SCALE[areaType] || 1) * (SUBITEM_SCALE[subitem] || 1) * periodScale(period);
  const r = (base, floor = 0) => Math.max(floor, Math.round(base * scale));

  const visits = r(128540);
  const patients = r(54210);
  const items = r(231880);
  const months = trendMonths();

  const subitemsForTrend = subitem === "ALL" ? ["D10", "D11", "D20"] : [subitem];
  const trend = [];
  months.forEach((m, mi) => {
    const growth = 0.55 + mi * 0.045; // rising over the year
    subitemsForTrend.forEach(s => {
      const share = s === "D10" ? 1 : s === "D11" ? 0.17 : 0.1;
      const v = r((10000 * share * growth) / 12) + Math.round(seedFrom(m + s) * 400);
      trend.push({ month: m, subitem: s, visits: supp(v) });
    });
  });

  const timeslot = [];
  for (let dow = 0; dow <= 6; dow += 1) {
    for (let hour = 8; hour <= 19; hour += 1) {
      const weekday = dow <= 4;
      const peak = hour >= 17 ? 1.5 : hour >= 10 && hour <= 11 ? 1.3 : 1;
      const dowW = weekday ? 1 : dow === 5 ? 0.8 : 0.4;
      const v = r((visits / 90) * peak * dowW * (0.7 + seedFrom(`${dow}-${hour}`) * 0.6));
      timeslot.push({ dow, hour, visits: supp(v) });
    }
  }

  const funnel = [
    { visitNo: 1, visits: supp(patients) },
    { visitNo: 2, visits: supp(Math.round(patients * 0.83)) },
    { visitNo: 3, visits: supp(Math.round(patients * 0.54)) },
  ];

  const treatTop = TREATMENTS.map((name, i) => ({ name, count: supp(r(TREAT_WEIGHTS[i])) }))
    .sort((a, b) => (typeof b.count === "number" ? b.count : 0) - (typeof a.count === "number" ? a.count : 0));

  const diagTop = DIAGS.map((code, i) => ({ code, count: supp(r(DIAG_WEIGHTS[i])) }))
    .sort((a, b) => (typeof b.count === "number" ? b.count : 0) - (typeof a.count === "number" ? a.count : 0));

  // diag x age (top 5 diags, share within each disease group)
  const ageProfiles = {
    "K02.1": [18, 31, 36, 21, 12, 1], "K05.1": [4, 17, 34, 44, 38, 2],
    "K03.0": [2, 9, 21, 26, 30, 1], "K04.7": [6, 20, 33, 24, 15, 1], "K08.1": [1, 3, 12, 27, 41, 2],
  };
  const diagAge = [];
  diagTop.slice(0, 5).forEach(d => {
    const profile = ageProfiles[d.code] || [10, 20, 30, 25, 14, 1];
    AGE_BANDS.forEach((band, bi) => {
      const cnt = r((DIAG_WEIGHTS[DIAGS.indexOf(d.code)] || 5000) * (profile[bi] / 100));
      diagAge.push({ code: d.code, ageBand: band, count: supp(cnt) });
    });
  });

  // treat x age (top 5 treatments, share within each treatment group)
  const treatAge = [];
  treatTop.slice(0, 5).forEach((t, ti) => {
    const profile = [12, 22, 32, 20, 13, 1].map(x => x + ((ti * 3) % 7) - 3);
    AGE_BANDS.forEach((band, bi) => {
      const w = typeof t.count === "number" ? t.count : 5000;
      treatAge.push({ name: t.name, ageBand: band, count: supp(r(w * (Math.max(profile[bi], 1) / 100))) });
    });
  });

  const diagNames = {
    "K02.1": "Dental caries (dentine)", "K05.1": "Chronic gingivitis",
    "K03.0": "Attrition of teeth", "K04.7": "Periapical abscess", "K08.1": "Loss of teeth",
  };

  const demographics = [];
  AGE_BANDS.forEach(band => {
    const bandShare = AGE_WEIGHTS[band] / 100;
    [
      { gender: "หญิง", g: 0.58 },
      { gender: "ชาย", g: 0.4 },
      { gender: "ไม่ระบุ", g: 0.02 },
    ].forEach(({ gender, g }) => {
      demographics.push({ ageBand: band, gender, patients: supp(r(patients * bandShare * g)) });
    });
  });

  const newReturning = months.map((m, mi) => ({
    month: m,
    newPatients: supp(r((1800 + mi * 60) / 3)),
    returning: supp(r((3100 + mi * 380) / 3)),
  }));

  return {
    dataAsOf: Date.now(),
    uniquesAsOf: Date.now(),
    filters: { period, areaType, area, subitem },
    kpi: {
      visits: supp(visits),
      patients: supp(patients),
      items: supp(items),
      dentists: supp(r(1240)),
      branches: supp(r(312, 1)),
      provinces: provincesCount(areaType, firstArea),
    },
    areaRanking: buildAreaRanking(areaType, firstArea, scale),
    trend,
    timeslot,
    funnel,
    treatTop,
    treatAge,
    diagTop,
    diagAge,
    diagNames,
    demographics,
    newReturning,
  };
}

export default buildFixture;
